import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  CogIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  XMarkIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/apiClient';
import { buildApiUrl } from '../../utils/apiBaseUrl';
import CustomerSharePointCredentialsForm from '../customers/CustomerCredentialsForm';
import { calculateWeightedPhaseProgress } from '../../../../shared/phase-progress';

interface Phase {
  id: string;
  customerId: string;
  name: string;
  type: 'migration' | 'uat' | 'production';
  status: 'draft' | 'in_progress' | 'completed' | 'failed';
  description?: string;
  createdAt: string;
  updatedAt: string;
  ftsd?: string;
}

const PhaseRow: React.FC<{
  phase: Phase;
  customerId: string;
  onEdit: (phase: Phase) => void;
  getPhaseIcon: (status: string) => React.ReactNode;
  getStatusColor: (status: string) => string;
  getTypeColor: (type: string) => string;
}> = ({ phase, customerId, onEdit, getPhaseIcon, getStatusColor, getTypeColor }) => {
  const { data: wizardStatus, isLoading: isWizardLoading } = useQuery({
    queryKey: ['wizard-status', customerId, phase.id],
    queryFn: () => apiClient.getWizardStatus(customerId, phase.id),
  });

  const progress = useMemo(() => {
    if (!wizardStatus || !wizardStatus.steps || wizardStatus.steps.length === 0) {
      return { percent: 0, label: '0%', nextStep: 'Ready to start' };
    }
    const weightedProgress = calculateWeightedPhaseProgress(wizardStatus.steps);
    const percent = weightedProgress.percentage;
    const nextStep = wizardStatus.steps.find((s: any) => s.status === 'available')?.label || 'Migration complete';
    return { percent, label: `${percent}%`, nextStep };
  }, [wizardStatus]);

  const displayStatus = useMemo(() => {
    if (phase.status === 'failed') return 'failed';
    if (!wizardStatus || !wizardStatus.steps) return phase.status;

    // Dynamic status based on mapping progress
    const mappingStep = wizardStatus.steps.find((s: any) => s.stepId === 'mapping');
    if (mappingStep) {
      if (mappingStep.status === 'completed') return 'completed';
      if (mappingStep.status === 'in_progress') return 'in_progress';
      return 'draft';
    }
    return phase.status;
  }, [phase.status, wizardStatus]);

  return (
    <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all relative">
      <div className="flex flex-col lg:flex-row lg:items-center gap-6">
        {/* Phase Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            {getPhaseIcon(displayStatus)}
            <h3 className="text-xl font-bold text-gray-900 truncate">{phase.name}</h3>
            <button
              onClick={() => onEdit(phase)}
              className="p-1 text-gray-400 hover:text-brand-red transition-colors"
              title="Edit Phase"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${getTypeColor(phase.type)}`}>
              {phase.type.toUpperCase()}
            </span>
            <span className={`px-2.5 py-0.5 text-xs font-semibold rounded border ${getStatusColor(displayStatus)}`}>
              {displayStatus.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          {phase.description ? (
            <p className="text-gray-600 text-sm line-clamp-1 mb-1">{phase.description}</p>
          ) : (
            <p className="text-gray-400 text-sm italic mb-1">No description</p>
          )}
          {phase.ftsd && (
            <p className="text-xs font-medium text-gray-500">
              FTSD: {new Date(phase.ftsd).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Progress Section */}
        <div className="flex-1 max-w-sm w-full bg-gray-50/50 p-4 rounded-xl border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Migration Progress</span>
            <span className="text-sm font-bold text-brand-red">{progress.label}</span>
          </div>
          <div className="h-3 w-full bg-gray-200 rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-brand-red shadow-sm transition-all duration-700 ease-in-out"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse" />
            <p className="text-xs font-medium text-gray-600 truncate">
              {isWizardLoading ? 'Loading progress...' : `Next: ${progress.nextStep}`}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`/customers/${customerId}/phases/${phase.id}/wizard`}
            className="btn-primary"
          >
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Mapping Wizard
          </Link>
          <Link
            to={`/customers/${customerId}/phases/${phase.id}`}
            className="btn-secondary"
          >
            More phase options
          </Link>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-4 pt-4 border-t border-gray-50 flex justify-between text-[10px] text-gray-400 uppercase tracking-wider">
        <span>Created: {new Date(phase.createdAt).toLocaleString()}</span>
        <span>Last Updated: {new Date(phase.updatedAt).toLocaleString()}</span>
      </div>
    </div>
  );
};

const PhaseManager: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPhase, setNewPhase] = useState({
    name: '',
    type: 'migration' as const,
    description: '',
    ftsd: ''
  });

  // Edit State
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState<Phase | null>(null);
  const [editForm, setEditForm] = useState({ name: '', type: 'migration' as Phase['type'], description: '', ftsd: '' });

  // SharePoint Credentials Modal State
  const [isSharePointModalOpen, setIsSharePointModalOpen] = useState(false);

  type CatalogItem = { Step: number; Object: string; Entity: string; Required: boolean };
  type SelectionItem = { Step: number; Object: string; Entity: string; Required: boolean; selected: boolean };
  const [entityCatalog, setEntityCatalog] = useState<CatalogItem[]>([]);
  const [selection, setSelection] = useState<SelectionItem[]>([]);
  const [entitiesError, setEntitiesError] = useState<string>('');

  useEffect(() => {
    const loadEntities = async () => {
      try {
        let data: any[] = [];
        let res = await fetch(buildApiUrl('/entities.json'), { cache: 'no-store' });
        if (!res.ok) res = await fetch('/entities.json', { cache: 'no-store' });
        if (!res.ok) throw new Error(`Failed to load entities.json (${res.status})`);
        data = await res.json();
        setEntityCatalog(data);
        setSelection(data.map((i: CatalogItem) => ({ ...i, selected: true })));
        setEntitiesError('');
      } catch (e) {
        setEntitiesError('Could not load entities.json. Start backend (7071) or ensure root entities.json is present.');
      }
    };
    loadEntities();
  }, []);

  // Fetch customer info
  const { data: customer } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => apiClient.getCustomer(customerId!),
    enabled: !!customerId
  });

  // Fetch phases
  const { data: phasesRaw, isLoading } = useQuery({
    queryKey: ['phases', customerId],
    queryFn: () => apiClient.getPhases(customerId!),
    enabled: !!customerId
  });

  // Deduplicate phases by ID to prevent duplicate display
  const phases = React.useMemo(() => {
    if (!phasesRaw || !Array.isArray(phasesRaw)) return [];

    const uniquePhases = new Map<string, Phase>();
    phasesRaw.forEach((phase: Phase) => {
      if (phase && phase.id) {
        // Keep the phase with the latest updatedAt if duplicates exist
        const existing = uniquePhases.get(phase.id);
        if (!existing || new Date(phase.updatedAt) > new Date(existing.updatedAt)) {
          uniquePhases.set(phase.id, phase);
        }
      }
    });

    return Array.from(uniquePhases.values()).sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [phasesRaw]);

  // Create phase mutation
  const createPhaseMutation = useMutation({
    mutationFn: (phaseData: typeof newPhase) => apiClient.createPhase(customerId!, phaseData),
    onSuccess: () => {
      toast.success('Phase created successfully');
      setShowCreateForm(false);
      setNewPhase({ name: '', type: 'migration', description: '', ftsd: '' });
      queryClient.invalidateQueries({ queryKey: ['phases', customerId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to create phase');
    }
  });

  const handleCreatePhase = (e: React.FormEvent) => {
    e.preventDefault();
    createPhaseMutation.mutate(newPhase, {
      onSuccess: async (phase: any) => {
        // After phase is created, save selected entities with full details
        const entitiesPayload = selection.map(e => ({
          Step: e.Step,
          Object: e.Object,
          Entity: e.Entity,
          Required: e.Required,
          selected: e.selected || e.Required
        }));
        try {
          await apiClient.savePhaseEntities(customerId!, phase.id, entitiesPayload);
        } catch { }
      }
    });
  };

  const openEditModal = (phase: Phase) => {
    setEditingPhase(phase);
    setEditForm({
      name: phase.name,
      type: phase.type,
      description: phase.description || '',
      ftsd: phase.ftsd || ''
    });
    setIsEditOpen(true);
  };

  const handleUpdatePhase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId || !editingPhase) return;

    try {
      await apiClient.updatePhase(customerId, editingPhase.id, {
        name: editForm.name,
        type: editForm.type,
        description: editForm.description,
        ftsd: editForm.ftsd
      });
      toast.success('Phase updated successfully');
      queryClient.invalidateQueries({ queryKey: ['phases', customerId] });
      setIsEditOpen(false);
      setEditingPhase(null);
    } catch (err: any) {
      console.error(err);
      toast.error('Failed to update phase');
    }
  };

  const getPhaseIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-6 h-6 text-green-600" />;
      case 'in_progress':
        return <ArrowPathIcon className="w-6 h-6 text-blue-600" />;
      case 'failed':
        return <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />;
      default:
        return <ClockIcon className="w-6 h-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'production':
        return 'bg-red-100 text-red-800';
      case 'uat':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <nav className="text-sm text-gray-500 mb-2">
            <Link to="/customers" className="hover:text-gray-700">Customers</Link>
            <span className="mx-2">/</span>
            <span>{customer?.name}</span>
          </nav>
          <h1 className="text-3xl font-bold text-gray-900">Migration Phases</h1>
          <p className="text-gray-600 mt-2">Manage migration phases for {customer?.name}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsSharePointModalOpen(true)}
            className="btn-secondary flex items-center"
          >
            <CloudIcon className="w-5 h-5 mr-2" />
            SharePoint Credentials
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            New Phase
          </button>
        </div>
      </div>

      {/* Create Phase Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Create New Phase</h2>
          <form onSubmit={handleCreatePhase} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Phase Name</label>
                <input
                  type="text"
                  value={newPhase.name}
                  onChange={(e) => setNewPhase({ ...newPhase, name: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Initial Migration, UAT Testing"
                  required
                />
              </div>

              <div>
                <label className="form-label">Phase Type</label>
                <select
                  value={newPhase.type}
                  onChange={(e) => setNewPhase({ ...newPhase, type: e.target.value as any })}
                  className="form-input"
                >
                  <option value="migration">Migration</option>
                  <option value="uat">UAT (User Acceptance Testing)</option>
                  <option value="production">Production</option>
                </select>
              </div>

              <div>
                <label className="form-label">Full Transmission Start Date (FTSD)</label>
                <input
                  type="date"
                  value={newPhase.ftsd}
                  onChange={(e) => setNewPhase({ ...newPhase, ftsd: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>

            <div>
              <label className="form-label">Description</label>
              <textarea
                value={newPhase.description}
                onChange={(e) => setNewPhase({ ...newPhase, description: e.target.value })}
                className="form-input"
                rows={3}
                placeholder="Describe the purpose and scope of this phase"
              />
            </div>

            {/* Entity selection */}
            {entitiesError && (
              <div className="text-sm text-red-600">{entitiesError}</div>
            )}
            {entityCatalog.length > 0 && (
              <div>
                <label className="form-label">Entities to process</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-auto p-2 border rounded">
                  {selection.map(item => (
                    <label key={item.Entity} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={item.selected}
                        disabled={item.Required}
                        onChange={(e) => setSelection(prev => prev.map(p => p.Entity === item.Entity ? { ...p, selected: e.target.checked } : p))}
                      />
                      <span>
                        <span className="font-medium">{item.Step}. {item.Entity}</span>
                        <span className="ml-2 text-gray-500">({item.Object})</span>
                        {item.Required && <span className="ml-2 text-red-600">required</span>}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Required entities are always included.</p>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="submit"
                className="btn-primary"
                disabled={createPhaseMutation.isPending}
              >
                {createPhaseMutation.isPending ? 'Creating...' : 'Create Phase'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Phases List */}
      {phases && phases.length > 0 ? (
        <div className="space-y-4">
          {phases.map((phase: Phase) => (
            <PhaseRow
              key={phase.id}
              phase={phase}
              customerId={customerId!}
              onEdit={openEditModal}
              getPhaseIcon={getPhaseIcon}
              getStatusColor={getStatusColor}
              getTypeColor={getTypeColor}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <FolderOpenIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No phases found</h3>
          <p className="text-gray-600 mb-6">Get started by creating your first migration phase</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary"
          >
            Create First Phase
          </button>
        </div>
      )}

      {/* Edit Phase Modal */}
      <Transition appear show={isEditOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsEditOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Edit Phase Details
                    </Dialog.Title>
                    <button
                      onClick={() => setIsEditOpen(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <form onSubmit={handleUpdatePhase} className="space-y-4">
                    <div>
                      <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                        Phase Name
                      </label>
                      <input
                        type="text"
                        id="edit-name"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="edit-type" className="block text-sm font-medium text-gray-700">
                        Phase Type
                      </label>
                      <select
                        id="edit-type"
                        value={editForm.type}
                        onChange={(e) => setEditForm({ ...editForm, type: e.target.value as any })}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm bg-white"
                      >
                        <option value="migration">Migration</option>
                        <option value="uat">UAT (User Acceptance Testing)</option>
                        <option value="production">Production</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        id="edit-description"
                        rows={3}
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="edit-ftsd" className="block text-sm font-medium text-gray-700">
                        Full Transmission Start Date (FTSD)
                      </label>
                      <input
                        type="date"
                        id="edit-ftsd"
                        value={editForm.ftsd}
                        onChange={(e) => setEditForm({ ...editForm, ftsd: e.target.value })}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                      />
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        onClick={() => setIsEditOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* SharePoint Credentials Modal */}
      <Transition appear show={isSharePointModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsSharePointModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all relative">
                  <button
                    onClick={() => setIsSharePointModalOpen(false)}
                    className="absolute top-6 right-6 text-gray-400 hover:text-gray-500 z-10"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>

                  {customerId && (
                    <CustomerSharePointCredentialsForm
                      customerId={customerId}
                      onClose={() => setIsSharePointModalOpen(false)}
                      isEmbedded={true}
                    />
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default PhaseManager;
